import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

export const PrewarmedDomainsAdmin: React.FC = () => {
  const { user } = useAuth();
  const [domainName, setDomainName] = useState('');
  const [emailAccount, setEmailAccount] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [statusFilter, setStatusFilter] = useState<'available' | 'ordered' | ''>('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchItems();
  }, [pagination.page, pagination.limit, statusFilter, search]);

  const fetchItems = async () => {
    try {
      const response = await apiService.getPrewarmedDomains({
        page: pagination.page,
        limit: pagination.limit,
        status: (statusFilter as any) || undefined,
        search: search || undefined,
      });
      setItems(response.data);
      setPagination(response.pagination);
    } catch (e) {
      setError('Failed to load inventory');
    }
  };

  const resetForm = () => {
    setDomainName('');
    setEmailAccount('');
    setFirstName('');
    setLastName('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!domainName || !emailAccount) {
      setError('domain_name and email_account are required');
      return;
    }
    setSubmitting(true);
    try {
      await apiService.createPrewarmedDomain({
        domain_name: domainName.trim(),
        email_account: emailAccount.trim(),
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
      });
      resetForm();
      // refresh list, reset to first page
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchItems();
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || user.userType !== 'superadmin') {
    return (
      <div className="card-modern p-6">
        <h1 className="text-xl font-semibold">Access Denied</h1>
        <p className="text-sm text-muted-foreground mt-2">Only super admins can access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Pre-warmed Domains Inventory</h1>
        <p className="mt-2 text-sm text-muted-foreground">Add and review pre-warmed domain mailboxes.</p>
      </div>

      <form onSubmit={handleSubmit} className="card-modern p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Domain Name</label>
            <input
              className="input-modern h-10 w-full px-3 text-sm"
              placeholder="example.com"
              value={domainName}
              onChange={(e) => setDomainName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Email Account</label>
            <input
              className="input-modern h-10 w-full px-3 text-sm"
              placeholder="sarah@example.com"
              value={emailAccount}
              onChange={(e) => setEmailAccount(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">First Name</label>
            <input
              className="input-modern h-10 w-full px-3 text-sm"
              placeholder="Sarah"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Last Name</label>
            <input
              className="input-modern h-10 w-full px-3 text-sm"
              placeholder="Connor"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        {error && <div className="text-destructive text-sm">{error}</div>}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            className="btn-primary px-4 py-2 rounded-lg text-sm font-medium"
            disabled={submitting}
          >
            {submitting ? 'Saving…' : 'Add Inventory'}
          </button>
        </div>
      </form>

      <div className="card-modern p-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex gap-3">
            <select
              className="input-modern h-10 px-3 text-sm min-w-[150px]"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as any);
                setPagination(p => ({ ...p, page: 1 }));
              }}
            >
              <option value="">All</option>
              <option value="available">Available</option>
              <option value="ordered">Ordered</option>
            </select>
            <input
              className="input-modern h-10 px-3 text-sm min-w-[240px]"
              placeholder="Search domain or email"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination(p => ({ ...p, page: 1 }));
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Show</span>
            <select
              className="input-modern h-10 px-3 text-sm"
              value={pagination.limit}
              onChange={(e) => setPagination(p => ({ ...p, page: 1, limit: Number(e.target.value) }))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-muted-foreground">per page</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Domain</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">First</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Last</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Ordered At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3 text-sm">{item.domain_name}</td>
                  <td className="px-4 py-3 text-sm">{item.email_account}</td>
                  <td className="px-4 py-3 text-sm">{item.first_name || '-'}</td>
                  <td className="px-4 py-3 text-sm">{item.last_name || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${item.status === 'available' ? 'bg-green-500/10 text-green-500' : 'bg-amber-500/10 text-amber-500'}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{item.ordered_at ? new Date(item.ordered_at).toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border pt-4">
            <button
              className="px-3 py-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50"
              disabled={pagination.page === 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
            >
              Previous
            </button>
            <span className="text-sm">Page {pagination.page} of {pagination.totalPages}</span>
            <button
              className="px-3 py-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50"
              disabled={pagination.page === pagination.totalPages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrewarmedDomainsAdmin;


